/**
 * SpinResultDisplay Component
 *
 * Shows the result of a spin with celebratory animations.
 * Displays different effects based on prize size.
 */

import { memo, useEffect, useState } from 'react';
import type { SpinResult } from '../../types/jackpot';
import { useTranslation } from '../../hooks/useTranslation';
import { publicAsset } from '../../utils/assetUrl';

interface SpinResultDisplayProps {
  result: SpinResult;
  onDismiss?: () => void;
  className?: string;
}

export const SpinResultDisplay = memo(function SpinResultDisplay({
  result,
  onDismiss,
  className = '',
}: SpinResultDisplayProps) {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);
  const isJackpot = result.jackpot_wheel !== null;
  const isBigWin = result.total_tokens_won >= 100;

  useEffect(() => {
    // Show confetti for jackpot or big wins
    if (isJackpot || isBigWin) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isJackpot, isBigWin]);

  return (
    <div className={`relative text-center animate-slide-up ${className}`}>
      {/* Confetti effect for big wins */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#9B59B6', '#3498DB'][
                  Math.floor(Math.random() * 5)
                ],
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1.5 + Math.random()}s`,
              }}
            />
          ))}
        </div>
      )}

      {isJackpot ? (
        <>
          {/* Jackpot win display */}
          <div className="text-5xl mb-4 animate-bounce">
            <span role="img" aria-label="jackpot">
              {'\u{1F3B0}\u{1F4B0}\u{1F3B0}'}
            </span>
          </div>
          <h3 className="text-3xl font-bold text-amber-400 mb-2 animate-golden-shimmer">
            {t('jackpot.jackpotWin', 'JACKPOT WIN!')}
          </h3>
          <p className="text-xl text-amber-300 mb-2">
            {result.jackpot_wheel!.percentage}% {t('jackpot.ofPool', 'of pool')}
          </p>
          <p className="text-4xl font-bold text-green-400 mt-4 animate-pulse">
            +{result.total_tokens_won.toLocaleString()} {t('common.tokens', 'tokens')}{' '}
            <img
              src={publicAsset('token-coin-32.png')}
              alt=""
              className="h-8 w-8 inline"
              draggable={false}
            />
            !
          </p>
        </>
      ) : (
        <>
          {/* Regular win display */}
          <div className="text-4xl mb-4">
            <span role="img" aria-label="celebration">
              {isBigWin ? '\u{1F389}' : '\u{2728}'}
            </span>
          </div>
          <h3
            className={`text-2xl font-bold mb-2 ${isBigWin ? 'text-amber-400' : 'text-slate-200'}`}
          >
            {t('jackpot.youWon', 'You won!')}
          </h3>
          <p
            className={`text-3xl font-bold mt-2 ${isBigWin ? 'text-green-400 animate-pulse' : 'text-green-400'}`}
          >
            +{result.total_tokens_won.toLocaleString()} {t('common.tokens', 'tokens')}{' '}
            <img
              src={publicAsset('token-coin-32.png')}
              alt=""
              className="h-6 w-6 inline"
              draggable={false}
            />
          </p>
        </>
      )}

      {/* New balance */}
      <p className="text-sm text-slate-400 mt-6">
        {t('jackpot.newBalance', 'New balance')}: {result.new_token_balance.toLocaleString()}{' '}
        {t('common.tokens', 'tokens')}{' '}
        <img
          src={publicAsset('token-coin-32.png')}
          alt=""
          className="h-3.5 w-3.5 inline"
          draggable={false}
        />
      </p>

      {/* Tickets remaining */}
      <p className="text-sm text-slate-500 mt-2">
        {t('jackpot.ticketsRemaining', 'Tickets remaining')}: {result.remaining_tickets}
      </p>

      {/* Dismiss button */}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="mt-6 px-6 py-2 rounded-lg
                     bg-secondary-700 hover:bg-secondary-600
                     text-white text-sm font-medium
                     transition-colors duration-200"
        >
          {result.remaining_tickets > 0
            ? t('jackpot.spinAgain', 'Spin Again')
            : t('common.close', 'Close')}
        </button>
      )}
    </div>
  );
});
